import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from './config';
import { formatLocalDateTime } from './timezone';


const containerStyle = {
  padding: '40px',
  maxWidth: '800px',
  margin: '0 auto',
  width: '100%',
  color: 'var(--text-primary)'
};

const cardStyle = {
  background: 'var(--bg-secondary)',
  padding: '20px',
  borderRadius: '8px',
  border: '1px solid var(--border-color)',
  marginBottom: '20px'
};

const inputStyle = {
  width: '100%',
  padding: '10px',
  borderRadius: '4px',
  border: '1px solid var(--border-color)',
  background: 'var(--bg-input)',
  color: 'var(--text-primary)',
  marginBottom: '15px',
  boxSizing: 'border-box'
};

const btnStyle = (bg, disabled = false) => ({
  padding: '10px 20px',
  background: disabled ? 'var(--bg-tertiary)' : bg,
  color: disabled ? 'var(--text-tertiary)' : 'var(--text-inverse)',
  border: 'none',
  borderRadius: '4px',
  cursor: disabled ? 'not-allowed' : 'pointer',
  fontSize: '14px',
  fontWeight: '500',
  transition: 'background 0.2s',
  width: '100%'
});

export default function Profile({ onBack, token }) {
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  // API Keys state
  const [apiKeys, setApiKeys] = useState([]);
  const [keysLoading, setKeysLoading] = useState(true);
  const [newKeyName, setNewKeyName] = useState('');
  const [creatingKey, setCreatingKey] = useState(false);
  const [createdKey, setCreatedKey] = useState(null); // shown once after creation
  const [keysMessage, setKeysMessage] = useState(null);
  const [revokingId, setRevokingId] = useState(null);
  const [copiedKey, setCopiedKey] = useState(false);

  // MCP Server state
  const [workspaces, setWorkspaces] = useState([]);
  const [mcpWorkspaceId, setMcpWorkspaceId] = useState('');
  const [copiedMcp, setCopiedMcp] = useState(false);

  // 2FA state
  const [totpEnabled, setTotpEnabled] = useState(false);
  const [totpLoading, setTotpLoading] = useState(true);
  const [totpSetupData, setTotpSetupData] = useState(null); // {secret, uri, qr_image}
  const [totpCode, setTotpCode] = useState('');
  const [totpMsg, setTotpMsg] = useState(null);
  const [totpAction, setTotpAction] = useState(null); // null | 'setup' | 'disable'

  const authHeaders = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchApiKeys();
    axios.get(`${API_BASE_URL}/workspaces/`, { headers: authHeaders })
      .then(res => {
        const ws = Array.isArray(res.data) ? res.data : [];
        setWorkspaces(ws);
        if (ws.length > 0) setMcpWorkspaceId(ws[0].id);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    axios.get(`${API_BASE_URL}/users/me`, { headers: authHeaders })
      .then(res => { setTotpEnabled(res.data.totp_enabled || false); setTotpLoading(false); })
      .catch(() => setTotpLoading(false));
  }, []);

  const fetchApiKeys = async () => {
    setKeysLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api-keys`, { headers: authHeaders });
      setApiKeys(res.data);
    } catch (err) {
      setKeysMessage({ type: 'error', text: 'Failed to load API keys' });
    } finally {
      setKeysLoading(false);
    }
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: "New passwords do not match" });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      await axios.put(`${API_BASE_URL}/users/me/password`, {
        old_password: oldPassword,
        new_password: newPassword
      }, {
        headers: authHeaders
      });
      setMessage({ type: 'success', text: "Password updated successfully!" });
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      console.error("Failed to update password", error);
      setMessage({
        type: 'error',
        text: error.response?.data?.detail || "Failed to update password"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateKey = async (e) => {
    e.preventDefault();
    if (!newKeyName.trim()) return;
    setCreatingKey(true);
    setKeysMessage(null);
    setCreatedKey(null);
    try {
      const res = await axios.post(`${API_BASE_URL}/api-keys`, { name: newKeyName.trim() }, { headers: authHeaders });
      setCreatedKey(res.data.key);
      setNewKeyName('');
      await fetchApiKeys();
    } catch (err) {
      setKeysMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to create API key' });
    } finally {
      setCreatingKey(false);
    }
  };

  const handleRevoke = async (keyId) => {
    setRevokingId(keyId);
    try {
      await axios.delete(`${API_BASE_URL}/api-keys/${keyId}`, { headers: authHeaders });
      setApiKeys(prev => prev.filter(k => k.id !== keyId));
      if (keysMessage) setKeysMessage(null);
    } catch (err) {
      setKeysMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to revoke API key' });
    } finally {
      setRevokingId(null);
    }
  };

  const handleCopyKey = () => {
    if (!createdKey) return;
    navigator.clipboard.writeText(createdKey).then(() => {
      setCopiedKey(true);
      setTimeout(() => setCopiedKey(false), 2000);
    });
  };

  const startTotpSetup = async () => {
    setTotpMsg(null);
    setTotpCode('');
    try {
      const res = await axios.post(`${API_BASE_URL}/users/me/totp/setup`, {}, { headers: authHeaders });
      setTotpSetupData(res.data);
      setTotpAction('setup');
    } catch (err) {
      setTotpMsg({ type: 'error', text: err.response?.data?.detail || 'Failed to start 2FA setup' });
    }
  };

  const confirmTotpEnable = async (e) => {
    e.preventDefault();
    setTotpMsg(null);
    try {
      await axios.post(`${API_BASE_URL}/users/me/totp/enable`, { secret: totpSetupData.secret, code: totpCode.replace(/\s/g, '') }, { headers: authHeaders });
      setTotpEnabled(true);
      setTotpAction(null);
      setTotpSetupData(null);
      setTotpCode('');
      setTotpMsg({ type: 'success', text: '2FA enabled. You will now need your authenticator app at login.' });
    } catch (err) {
      setTotpMsg({ type: 'error', text: err.response?.data?.detail || 'Invalid code — try again' });
      setTotpCode('');
    }
  };

  const confirmTotpDisable = async (e) => {
    e.preventDefault();
    setTotpMsg(null);
    try {
      await axios.delete(`${API_BASE_URL}/users/me/totp`, { headers: authHeaders, data: { code: totpCode.replace(/\s/g, '') } });
      setTotpEnabled(false);
      setTotpAction(null);
      setTotpCode('');
      setTotpMsg({ type: 'success', text: '2FA disabled.' });
    } catch (err) {
      setTotpMsg({ type: 'error', text: err.response?.data?.detail || 'Invalid code — try again' });
      setTotpCode('');
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    return formatLocalDateTime(iso);
  };

  const msgBox = (msg) => msg ? (
    <div style={{
      padding: '10px',
      borderRadius: '4px',
      marginBottom: '15px',
      background: msg.type === 'error' ? 'rgba(220, 53, 69, 0.1)' : 'rgba(40, 167, 69, 0.1)',
      color: msg.type === 'error' ? 'var(--error-color)' : 'var(--success-color)',
      border: `1px solid ${msg.type === 'error' ? 'var(--error-color)' : 'var(--success-color)'}`
    }}>{msg.text}</div>
  ) : null;

  return (
    <div style={{ width: '100vw', height: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-primary)', overflow: 'auto' }}>
      <div style={{ padding: '20px', borderBottom: '1px solid var(--border-color)', background: 'var(--bg-tertiary)', display: 'flex', alignItems: 'center' }}>
        <button onClick={onBack} style={{ background: 'transparent', border: 'none', color: 'var(--text-tertiary)', cursor: 'pointer', marginRight: '10px', fontSize: '16px' }}>
          ← Back
        </button>
        <h2 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '20px' }}>User Profile</h2>
      </div>

      <div style={containerStyle}>
        {/* Change Password */}
        <div style={cardStyle}>
          <h3 style={{ marginTop: 0, marginBottom: '20px', color: 'var(--text-primary)' }}>Change Password</h3>

          {msgBox(message)}

          <form onSubmit={handleUpdatePassword}>
            <div style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', marginBottom: '5px', color: 'var(--text-secondary)' }}>Current Password</label>
              <input
                type="password"
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                style={inputStyle}
                required
              />
            </div>

            <div style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', marginBottom: '5px', color: 'var(--text-secondary)' }}>New Password</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                style={inputStyle}
                required
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', color: 'var(--text-secondary)' }}>Confirm New Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                style={inputStyle}
                required
              />
            </div>

            <button type="submit" style={btnStyle('var(--accent-color)', loading)} disabled={loading}>
              {loading ? 'Updating...' : 'Update Password'}
            </button>
          </form>
        </div>

        {/* Two-Factor Authentication */}
        <div style={cardStyle}>
          <h3 style={{ marginTop: 0, marginBottom: '6px', color: 'var(--text-primary)' }}>Two-Factor Authentication</h3>
          <p style={{ marginTop: 0, marginBottom: '16px', fontSize: '13px', color: 'var(--text-secondary)' }}>
            Add an extra layer of security. When enabled, you will need a 6-digit code from an authenticator app (Google Authenticator, Authy, etc.) at every login.
          </p>
          {totpLoading ? (
            <div style={{ color: 'var(--text-tertiary)', fontSize: '13px' }}>Loading…</div>
          ) : (
            <>
              {msgBox(totpMsg)}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: totpAction ? '16px' : '0' }}>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 600,
                  background: totpEnabled ? 'rgba(40,167,69,0.12)' : 'rgba(108,117,125,0.12)',
                  color: totpEnabled ? 'var(--success-color)' : 'var(--text-tertiary)',
                  border: `1px solid ${totpEnabled ? 'var(--success-color)' : 'var(--border-color)'}`,
                }}>
                  {totpEnabled ? '🔒 Enabled' : '🔓 Disabled'}
                </span>
                {!totpAction && !totpEnabled && (
                  <button onClick={startTotpSetup} style={{ padding: '6px 14px', background: 'var(--accent-color)', color: 'var(--text-inverse)', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', fontWeight: 500 }}>
                    Enable 2FA
                  </button>
                )}
                {!totpAction && totpEnabled && (
                  <button onClick={() => { setTotpAction('disable'); setTotpCode(''); setTotpMsg(null); }} style={{ padding: '6px 14px', background: 'transparent', color: 'var(--error-color)', border: '1px solid var(--error-color)', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}>
                    Disable 2FA
                  </button>
                )}
              </div>

              {/* Setup flow */}
              {totpAction === 'setup' && totpSetupData && (
                <div style={{ marginTop: '16px', padding: '16px', background: 'var(--bg-primary)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                  <p style={{ margin: '0 0 12px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                    1. Scan the QR code with your authenticator app, or enter the key manually.
                  </p>
                  <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', marginBottom: '16px', flexWrap: 'wrap' }}>
                    <img src={totpSetupData.qr_image} alt="2FA QR Code" style={{ width: '140px', height: '140px', borderRadius: '6px', background: '#fff', padding: '4px', border: '1px solid var(--border-color)' }} />
                    <div style={{ flex: 1, minWidth: '180px' }}>
                      <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Manual entry key</div>
                      <div style={{ fontFamily: 'monospace', fontSize: '13px', color: 'var(--text-primary)', background: 'var(--bg-secondary)', padding: '8px 10px', borderRadius: '4px', border: '1px solid var(--border-color)', wordBreak: 'break-all', letterSpacing: '0.1em' }}>
                        {totpSetupData.secret}
                      </div>
                    </div>
                  </div>
                  <p style={{ margin: '0 0 10px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                    2. Enter the 6-digit code shown in your app to confirm setup.
                  </p>
                  <form onSubmit={confirmTotpEnable} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      value={totpCode}
                      onChange={(e) => setTotpCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                      placeholder="000000"
                      autoFocus
                      style={{ ...inputStyle, width: '120px', marginBottom: 0, textAlign: 'center', fontFamily: 'monospace', fontSize: '16px', letterSpacing: '0.3em' }}
                    />
                    <button
                      type="submit"
                      disabled={totpCode.length !== 6}
                      style={{ padding: '10px 18px', background: totpCode.length !== 6 ? 'var(--bg-tertiary)' : 'var(--success-color)', color: totpCode.length !== 6 ? 'var(--text-tertiary)' : 'var(--text-inverse)', border: 'none', borderRadius: '4px', cursor: totpCode.length !== 6 ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: 600, whiteSpace: 'nowrap' }}
                    >
                      Confirm & Enable
                    </button>
                    <button type="button" onClick={() => { setTotpAction(null); setTotpSetupData(null); setTotpCode(''); setTotpMsg(null); }} style={{ padding: '10px 12px', background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border-color)', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}>
                      Cancel
                    </button>
                  </form>
                </div>
              )}

              {/* Disable flow */}
              {totpAction === 'disable' && (
                <div style={{ marginTop: '16px', padding: '16px', background: 'rgba(244,67,54,0.05)', borderRadius: '8px', border: '1px solid var(--error-color)' }}>
                  <p style={{ margin: '0 0 12px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                    Enter your current authenticator code to confirm disabling 2FA.
                  </p>
                  <form onSubmit={confirmTotpDisable} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      value={totpCode}
                      onChange={(e) => setTotpCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                      placeholder="000000"
                      autoFocus
                      style={{ ...inputStyle, width: '120px', marginBottom: 0, textAlign: 'center', fontFamily: 'monospace', fontSize: '16px', letterSpacing: '0.3em' }}
                    />
                    <button
                      type="submit"
                      disabled={totpCode.length !== 6}
                      style={{ padding: '10px 18px', background: totpCode.length !== 6 ? 'var(--bg-tertiary)' : 'var(--error-color)', color: totpCode.length !== 6 ? 'var(--text-tertiary)' : '#fff', border: 'none', borderRadius: '4px', cursor: totpCode.length !== 6 ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: 600, whiteSpace: 'nowrap' }}
                    >
                      Disable 2FA
                    </button>
                    <button type="button" onClick={() => { setTotpAction(null); setTotpCode(''); setTotpMsg(null); }} style={{ padding: '10px 12px', background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border-color)', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}>
                      Cancel
                    </button>
                  </form>
                </div>
              )}
            </>
          )}
        </div>

        {/* API Keys */}
        <div style={cardStyle}>
          <h3 style={{ marginTop: 0, marginBottom: '6px', color: 'var(--text-primary)' }}>API Keys</h3>
          <p style={{ marginTop: 0, marginBottom: '20px', fontSize: '13px', color: 'var(--text-secondary)' }}>
            Long-lived tokens for use in CI/CD pipelines, scripts, and external integrations. Use as <code style={{ background: 'var(--bg-tertiary)', padding: '1px 4px', borderRadius: '3px' }}>Authorization: Bearer mf_live_...</code>
          </p>

          {msgBox(keysMessage)}

          {/* Created key banner */}
          {createdKey && (
            <div style={{ marginBottom: '20px', padding: '14px', borderRadius: '6px', background: 'rgba(40, 167, 69, 0.08)', border: '1px solid var(--success-color)' }}>
              <div style={{ fontSize: '13px', color: 'var(--success-color)', fontWeight: 600, marginBottom: '8px' }}>
                API key created — copy it now, it will not be shown again.
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  readOnly
                  value={createdKey}
                  style={{ ...inputStyle, marginBottom: 0, fontFamily: 'monospace', fontSize: '12px', flex: 1 }}
                  onClick={(e) => e.target.select()}
                />
                <button
                  onClick={handleCopyKey}
                  style={{ padding: '10px 16px', background: copiedKey ? 'var(--success-color)' : 'var(--accent-color)', color: 'var(--text-inverse)', border: 'none', borderRadius: '4px', cursor: 'pointer', whiteSpace: 'nowrap', fontSize: '13px' }}
                >
                  {copiedKey ? 'Copied!' : 'Copy'}
                </button>
                <button
                  onClick={() => setCreatedKey(null)}
                  style={{ padding: '10px 12px', background: 'var(--bg-tertiary)', color: 'var(--text-secondary)', border: '1px solid var(--border-color)', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}

          {/* Create new key form */}
          <form onSubmit={handleCreateKey} style={{ display: 'flex', gap: '8px', marginBottom: '24px' }}>
            <input
              type="text"
              placeholder="Key name (e.g. CI/CD Pipeline)"
              value={newKeyName}
              onChange={(e) => setNewKeyName(e.target.value)}
              style={{ ...inputStyle, marginBottom: 0, flex: 1 }}
              maxLength={100}
            />
            <button
              type="submit"
              disabled={creatingKey || !newKeyName.trim()}
              style={{ padding: '10px 18px', background: creatingKey || !newKeyName.trim() ? 'var(--bg-tertiary)' : 'var(--accent-color)', color: creatingKey || !newKeyName.trim() ? 'var(--text-tertiary)' : 'var(--text-inverse)', border: 'none', borderRadius: '4px', cursor: creatingKey || !newKeyName.trim() ? 'not-allowed' : 'pointer', fontSize: '14px', whiteSpace: 'nowrap' }}
            >
              {creatingKey ? 'Creating...' : 'Create Key'}
            </button>
          </form>

          {/* Keys list */}
          {keysLoading ? (
            <div style={{ color: 'var(--text-tertiary)', fontSize: '14px' }}>Loading...</div>
          ) : apiKeys.length === 0 ? (
            <div style={{ color: 'var(--text-tertiary)', fontSize: '14px' }}>No API keys yet.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                  <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-secondary)', fontWeight: 500 }}>Name</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-secondary)', fontWeight: 500 }}>Prefix</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-secondary)', fontWeight: 500 }}>Created</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-secondary)', fontWeight: 500 }}>Last Used</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-secondary)', fontWeight: 500 }}>Expires</th>
                  <th style={{ padding: '6px 8px' }}></th>
                </tr>
              </thead>
              <tbody>
                {apiKeys.map(k => (
                  <tr key={k.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                    <td style={{ padding: '8px 8px', color: 'var(--text-primary)' }}>{k.name}</td>
                    <td style={{ padding: '8px 8px', fontFamily: 'monospace', color: 'var(--text-secondary)' }}>{k.key_prefix}...</td>
                    <td style={{ padding: '8px 8px', color: 'var(--text-secondary)' }}>{formatDate(k.created_at)}</td>
                    <td style={{ padding: '8px 8px', color: 'var(--text-secondary)' }}>{formatDate(k.last_used_at)}</td>
                    <td style={{ padding: '8px 8px', color: k.expires_at ? 'var(--text-secondary)' : 'var(--text-tertiary)' }}>{formatDate(k.expires_at)}</td>
                    <td style={{ padding: '8px 8px', textAlign: 'right' }}>
                      <button
                        onClick={() => handleRevoke(k.id)}
                        disabled={revokingId === k.id}
                        style={{ padding: '4px 10px', background: 'transparent', color: 'var(--error-color)', border: '1px solid var(--error-color)', borderRadius: '4px', cursor: revokingId === k.id ? 'not-allowed' : 'pointer', fontSize: '12px', opacity: revokingId === k.id ? 0.5 : 1 }}
                      >
                        {revokingId === k.id ? 'Revoking...' : 'Revoke'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        {/* MCP Server */}
        {workspaces.length > 0 && (() => {
          const baseUrl = window.location.origin;
          const mcpUrl = mcpWorkspaceId ? `${baseUrl}/mcp/${mcpWorkspaceId}` : '';
          const keyPlaceholder = createdKey || (apiKeys.length > 0 ? `${apiKeys[0].key_prefix}...` : 'YOUR_API_KEY');
          const configSnippet = mcpUrl ? JSON.stringify({
            mcpServers: {
              xavvy: {
                url: mcpUrl,
                headers: { Authorization: `Bearer ${keyPlaceholder}` },
              },
            },
          }, null, 2) : '';

          const handleCopyMcp = () => {
            navigator.clipboard.writeText(configSnippet).then(() => {
              setCopiedMcp(true);
              setTimeout(() => setCopiedMcp(false), 2000);
            });
          };

          return (
            <div style={cardStyle}>
              <h3 style={{ marginTop: 0, marginBottom: '6px', color: 'var(--text-primary)' }}>MCP Server</h3>
              <p style={{ marginTop: 0, marginBottom: '16px', fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                Expose published workflows as tools for Claude Desktop, Cursor, Windsurf, and any
                MCP-compatible AI client. Each published workflow becomes a callable tool.
              </p>

              {workspaces.length > 1 && (
                <div style={{ marginBottom: '14px' }}>
                  <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '5px' }}>Workspace</label>
                  <select
                    value={mcpWorkspaceId}
                    onChange={e => setMcpWorkspaceId(e.target.value)}
                    style={{ padding: '7px 10px', background: 'var(--bg-input)', color: 'var(--text-primary)', border: '1px solid var(--border-color)', borderRadius: '5px', fontSize: '13px', width: '100%' }}
                  >
                    {workspaces.map(ws => (
                      <option key={ws.id} value={ws.id}>{ws.name}</option>
                    ))}
                  </select>
                </div>
              )}

              <div style={{ marginBottom: '14px' }}>
                <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '5px' }}>MCP endpoint URL</label>
                <input
                  readOnly
                  value={mcpUrl}
                  onClick={e => e.target.select()}
                  style={{ ...inputStyle, marginBottom: 0, fontFamily: 'monospace', fontSize: '12px' }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '5px' }}>
                  <label style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                    Claude Desktop config — paste into{' '}
                    <code style={{ background: 'var(--bg-tertiary)', padding: '1px 4px', borderRadius: '3px', fontSize: '11px' }}>claude_desktop_config.json</code>
                  </label>
                  <button
                    onClick={handleCopyMcp}
                    style={{ padding: '3px 10px', background: copiedMcp ? 'var(--success-color)' : 'var(--accent-color)', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '11px', whiteSpace: 'nowrap' }}
                  >
                    {copiedMcp ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <pre style={{
                  margin: 0,
                  padding: '12px',
                  background: 'var(--bg-tertiary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  fontFamily: 'monospace',
                  fontSize: '11px',
                  color: 'var(--text-secondary)',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                  lineHeight: 1.55,
                }}>
                  {configSnippet}
                </pre>
              </div>

              <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', lineHeight: 1.6 }}>
                <strong style={{ color: 'var(--text-secondary)' }}>Claude Desktop config path:</strong>{' '}
                macOS: <code style={{ background: 'var(--bg-tertiary)', padding: '1px 4px', borderRadius: '3px' }}>~/Library/Application Support/Claude/claude_desktop_config.json</code>
                {' · '}Windows: <code style={{ background: 'var(--bg-tertiary)', padding: '1px 4px', borderRadius: '3px' }}>%APPDATA%\Claude\claude_desktop_config.json</code>
                <br />Replace <code style={{ background: 'var(--bg-tertiary)', padding: '1px 4px', borderRadius: '3px' }}>YOUR_API_KEY</code> with a real API key from the section above. Only <strong>published</strong> workflows are exposed.
              </div>
            </div>
          );
        })()}

        </div>
      </div>
    </div>
  );
}
